import React, { useState, useEffect } from 'react';
import { 
  ShieldCheck, 
  Lock, 
  FileText, 
  Truck, 
  ArrowLeft, 
  Sliders, 
  Sparkles, 
  CheckCircle2, 
  Cookie, 
  Check, 
  RotateCcw, 
  ExternalLink, 
  UserCheck, 
  Award, 
  Clock, 
  HelpCircle,
  Scale,
  Shield,
  BadgeCheck,
  Eye,
  RefreshCw,
  Zap,
  PhoneCall,
  Mail,
  FileCheck2,
  PackageCheck,
  AlertCircle
} from 'lucide-react';
import { DistributorConfig } from '../types';

export type PolicyTabType = 'privacy' | 'pdpa' | 'cookie' | 'cookie-settings' | 'terms' | 'shipping';

interface PrivacyPolicyPageProps {
  distributor: DistributorConfig;
  onNavigateHome: () => void;
  initialTab?: PolicyTabType;
}

export const PrivacyPolicyPage: React.FC<PrivacyPolicyPageProps> = ({ 
  distributor, 
  onNavigateHome,
  initialTab = 'privacy'
}) => {
  const [activeTab, setActiveTab] = useState<PolicyTabType>(initialTab);
  const domain = 'GIFFFORYOU.COM';

  // Cookie Settings state
  const [necessaryCookies] = useState(true); // Always strictly required
  const [analyticsCookies, setAnalyticsCookies] = useState(() => {
    return localStorage.getItem('cookie_consent_analytics') !== 'false';
  });
  const [marketingCookies, setMarketingCookies] = useState(() => {
    return localStorage.getItem('cookie_consent_marketing') !== 'false';
  });
  const [saveSuccessMessage, setSaveSuccessMessage] = useState(false);

  // Sync tab with props or URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab') as PolicyTabType;
    const pathnameLower = window.location.pathname.toLowerCase();

    if (pathnameLower === '/pdpa') {
      setActiveTab('pdpa');
    } else if (pathnameLower === '/cookie-policy' || pathnameLower === '/cookie') {
      setActiveTab('cookie');
    } else if (pathnameLower === '/cookie-settings') {
      setActiveTab('cookie-settings');
    } else if (pathnameLower === '/terms' || pathnameLower === '/terms-of-service') {
      setActiveTab('terms');
    } else if (pathnameLower === '/shipping-policy' || pathnameLower === '/shipping') {
      setActiveTab('shipping');
    } else if (tabParam && ['privacy', 'pdpa', 'cookie', 'cookie-settings', 'terms', 'shipping'].includes(tabParam)) {
      setActiveTab(tabParam);
    } else if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const handleTabChange = (tab: PolicyTabType) => {
    setActiveTab(tab);
    let newPath = '/privacy-policy';
    if (tab === 'pdpa') newPath = '/pdpa';
    else if (tab === 'cookie') newPath = '/cookie-policy';
    else if (tab === 'cookie-settings') newPath = '/cookie-settings';
    else if (tab === 'terms') newPath = '/terms';
    else if (tab === 'shipping') newPath = '/shipping-policy';

    window.history.pushState({}, '', newPath);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveCookieSettings = () => {
    localStorage.setItem('cookie_consent_analytics', String(analyticsCookies));
    localStorage.setItem('cookie_consent_marketing', String(marketingCookies));
    localStorage.setItem('cookie_consent_saved', 'true');
    setSaveSuccessMessage(true);
    setTimeout(() => {
      setSaveSuccessMessage(false);
    }, 3500);
  };

  const handleResetCookieSettings = () => {
    setAnalyticsCookies(true);
    setMarketingCookies(true);
    localStorage.setItem('cookie_consent_analytics', 'true');
    localStorage.setItem('cookie_consent_marketing', 'true');
    localStorage.setItem('cookie_consent_saved', 'true');
    setSaveSuccessMessage(true);
    setTimeout(() => {
      setSaveSuccessMessage(false);
    }, 3500);
  };

  const navigateTo = (url: string) => {
    window.history.pushState({}, '', url);
    window.dispatchEvent(new Event('popstate'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex-1 bg-gradient-to-b from-slate-50 via-pink-50/20 to-white py-6 sm:py-10">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        
        {/* Breadcrumb / Top Bar */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={onNavigateHome}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs sm:text-sm font-bold text-slate-700 bg-white hover:bg-slate-100 rounded-full transition-all active:scale-95 border border-slate-200 cursor-pointer shadow-xs"
          >
            <ArrowLeft className="w-4 h-4 text-[#E60067]" />
            <span>กลับสู่หน้าร้าน</span>
          </button>
          
          <div className="flex items-center gap-2">
            <span className="text-[11px] sm:text-xs font-mono font-bold text-slate-600 bg-white px-3 py-1.5 rounded-full border border-slate-200 shadow-2xs">
              PORTAL: {domain}
            </span>
          </div>
        </div>

        {/* Main Card Container */}
        <div className="bg-white rounded-[28px] shadow-xl border border-slate-200/80 overflow-hidden">
          
          {/* Header Banner */}
          <div className="bg-gradient-to-r from-slate-900 via-slate-850 to-slate-800 text-white px-6 sm:px-8 py-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 -mt-12 -mr-12 w-64 h-64 bg-pink-500/15 rounded-full blur-3xl pointer-events-none" />
            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start sm:items-center gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-tr from-[#E60067] to-rose-400 flex items-center justify-center text-white shadow-lg shadow-rose-950/40 shrink-0">
                  <ShieldCheck className="w-7 h-7 sm:w-8 sm:h-8" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-xl sm:text-2xl font-black tracking-tight text-white">
                      ศูนย์นโยบายและความโปร่งใสในการบริการ
                    </h1>
                    <span className="text-xs font-mono font-bold px-2.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/30">
                      {domain}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-300 font-medium mt-1">
                    การคุ้มครองสิทธิ์ของผู้บริโภค มาตรฐาน PDPA สากล และข้อกำหนดการสั่งซื้อผลิตภัณฑ์กิฟฟารีน
                  </p>
                </div>
              </div>

              <div className="hidden lg:flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/10 border border-white/15 backdrop-blur-xs text-xs font-semibold text-emerald-300 shrink-0">
                <BadgeCheck className="w-4 h-4 text-emerald-400" />
                <span>ตรวจสอบและคุ้มครองตามกฎหมาย 100%</span>
              </div>
            </div>
          </div>

          {/* Tab Navigation Grid */}
          <div className="bg-slate-100/70 border-b border-slate-200 px-2 sm:px-4 pt-2 flex items-center gap-1 sm:gap-2 overflow-x-auto scrollbar-none shrink-0">
            <button
              onClick={() => handleTabChange('privacy')}
              className={`flex items-center gap-2 px-3.5 py-2.5 rounded-t-xl text-xs sm:text-sm font-bold border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'privacy'
                  ? 'bg-white text-slate-900 border-[#E60067] shadow-2xs font-black'
                  : 'text-slate-600 hover:text-slate-900 border-transparent hover:bg-white/50'
              }`}
            >
              <Lock className="w-3.5 h-3.5 text-slate-700" />
              <span>นโยบายความเป็นส่วนตัว (Privacy)</span>
            </button>

            <button
              onClick={() => handleTabChange('pdpa')}
              className={`flex items-center gap-2 px-3.5 py-2.5 rounded-t-xl text-xs sm:text-sm font-bold border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'pdpa'
                  ? 'bg-white text-slate-900 border-[#E60067] shadow-2xs font-black'
                  : 'text-slate-600 hover:text-slate-900 border-transparent hover:bg-white/50'
              }`}
            >
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              <span>การคุ้มครองข้อมูลส่วนบุคคล PDPA</span>
            </button>

            <button
              onClick={() => handleTabChange('cookie')}
              className={`flex items-center gap-2 px-3.5 py-2.5 rounded-t-xl text-xs sm:text-sm font-bold border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'cookie'
                  ? 'bg-white text-slate-900 border-[#E60067] shadow-2xs font-black'
                  : 'text-slate-600 hover:text-slate-900 border-transparent hover:bg-white/50'
              }`}
            >
              <Cookie className="w-3.5 h-3.5 text-amber-600" />
              <span>นโยบายการใช้คุกกี้ (Cookie)</span>
            </button>

            <button
              onClick={() => handleTabChange('cookie-settings')}
              className={`flex items-center gap-2 px-3.5 py-2.5 rounded-t-xl text-xs sm:text-sm font-bold border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'cookie-settings'
                  ? 'bg-white text-slate-900 border-[#E60067] shadow-2xs font-black'
                  : 'text-slate-600 hover:text-slate-900 border-transparent hover:bg-white/50'
              }`}
            >
              <Sliders className="w-3.5 h-3.5 text-pink-600" />
              <span>การตั้งค่าคุกกี้ (Settings)</span>
            </button>

            <button
              onClick={() => handleTabChange('terms')}
              className={`flex items-center gap-2 px-3.5 py-2.5 rounded-t-xl text-xs sm:text-sm font-bold border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'terms'
                  ? 'bg-white text-slate-900 border-[#E60067] shadow-2xs font-black'
                  : 'text-slate-600 hover:text-slate-900 border-transparent hover:bg-white/50'
              }`}
            >
              <FileText className="w-3.5 h-3.5 text-blue-600" />
              <span>เงื่อนไขและข้อตกลงการใช้บริการ</span>
            </button>

            <button
              onClick={() => handleTabChange('shipping')}
              className={`flex items-center gap-2 px-3.5 py-2.5 rounded-t-xl text-xs sm:text-sm font-bold border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'shipping'
                  ? 'bg-white text-slate-900 border-[#E60067] shadow-2xs font-black'
                  : 'text-slate-600 hover:text-slate-900 border-transparent hover:bg-white/50'
              }`}
            >
              <Truck className="w-3.5 h-3.5 text-rose-600" />
              <span>นโยบายจัดส่งและประกัน 7 วัน</span>
            </button>
          </div>

          {/* Content Body */}
          <div className="p-6 sm:p-10 space-y-8 text-slate-700 leading-relaxed text-sm sm:text-base">
            
            {/* 1. Privacy Policy Tab (Rewritten & Redesigned) */}
            {activeTab === 'privacy' && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <div className="p-5 rounded-2xl bg-gradient-to-r from-slate-50 to-pink-50/50 border border-slate-200 flex items-start gap-3.5 shadow-2xs">
                  <div className="w-10 h-10 rounded-xl bg-pink-100 text-[#E60067] flex items-center justify-center shrink-0">
                    <Lock className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-base sm:text-lg text-slate-900 mb-1">
                      กรอบนโยบายการรักษาความลับและความเป็นส่วนตัวของผู้ใช้บริการ
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600 font-medium">
                      มีผลบังคับใช้สำหรับทุกธุรกรรมบน <strong className="text-slate-900">{domain}</strong> — เรายึดมั่นหลักธรรมาภิบาลในการปกป้องข้อมูลของลูกค้าทุกท่านอย่างสูงสุด
                    </p>
                  </div>
                </div>

                {/* Section 1: Data Collection */}
                <div className="space-y-4">
                  <h4 className="font-bold text-slate-900 text-base sm:text-lg flex items-center gap-2 border-b border-slate-100 pb-2">
                    <FileCheck2 className="w-5 h-5 text-[#E60067]" />
                    <span>1. ประเภทข้อมูลส่วนบุคคลที่เข้าระบบการประมวลผล</span>
                  </h4>
                  <p className="text-slate-600 text-sm">
                    เพื่อให้การทำรายการจัดส่งผลิตภัณฑ์กิฟฟารีนและการคำนวณสิทธิประโยชน์ทางธุรกิจดำเนินไปได้อย่างถูกต้องสมบูรณ์ ทางเราจะจัดเก็บข้อมูลเท่าที่จำเป็นต่อการให้บริการเท่านั้น:
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 shadow-2xs flex flex-col justify-between">
                      <div>
                        <div className="w-8 h-8 rounded-lg bg-pink-100 text-pink-700 flex items-center justify-center font-bold text-xs mb-2.5">
                          01
                        </div>
                        <strong className="block text-slate-900 text-sm mb-1.5">ข้อมูลเพื่อการจัดส่งและติดต่อ</strong>
                        <p className="text-xs text-slate-600 leading-relaxed">
                          ชื่อผู้รับ, เบอร์โทรศัพท์สำหรับขนส่ง, ที่อยู่ปลายทางสำหรับส่งมอบพัสดุ และ LINE ID สำหรับรับเลขพัสดุ
                        </p>
                      </div>
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 shadow-2xs flex flex-col justify-between">
                      <div>
                        <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs mb-2.5">
                          02
                        </div>
                        <strong className="block text-slate-900 text-sm mb-1.5">ข้อมูลคำสั่งซื้อและสมาชิก</strong>
                        <p className="text-xs text-slate-600 leading-relaxed">
                          รายการผลิตภัณฑ์ที่เลือกซื้อ, รหัสสมาชิกร้านค้า, ยอดคะแนนสะสม PV และหลักฐานยืนยันยอดชำระ
                        </p>
                      </div>
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 shadow-2xs flex flex-col justify-between">
                      <div>
                        <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs mb-2.5">
                          03
                        </div>
                        <strong className="block text-slate-900 text-sm mb-1.5">ข้อมูลทางเทคนิคและการจดจำ</strong>
                        <p className="text-xs text-slate-600 leading-relaxed">
                          Session ตะกร้าสินค้า, ค่าความยินยอมคุกกี้ และข้อมูลอุปกรณ์ เพื่อการแสดงผลที่เสถียรและรวดเร็ว
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Usage Purpose */}
                <div className="space-y-4">
                  <h4 className="font-bold text-slate-900 text-base sm:text-lg flex items-center gap-2 border-b border-slate-100 pb-2">
                    <CheckCircle2 className="w-5 h-5 text-[#E60067]" />
                    <span>2. วัตถุประสงค์หลักในการประมวลผลข้อมูล</span>
                  </h4>
                  <div className="space-y-2.5 text-sm text-slate-600">
                    <div className="p-3 bg-white rounded-xl border border-slate-200/80 flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                      <span><strong>การจัดส่งตรงจากศูนย์:</strong> นำส่งข้อมูลที่อยู่ให้คลังสาขาเดลิเวอรี่และพนักงานขนส่งเพื่อนำส่งสินค้าถึงมือผู้รับ</span>
                    </div>
                    <div className="p-3 bg-white rounded-xl border border-slate-200/80 flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                      <span><strong>การรักษาสิทธิ์สมาชิก:</strong> บันทึกคะแนน PV เข้าสู่รหัสสมาชิกกิฟฟารีนเพื่อผลประโยชน์เงินปันผลตามแผนงาน</span>
                    </div>
                    <div className="p-3 bg-white rounded-xl border border-slate-200/80 flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                      <span><strong>การดูแลหลังการขาย:</strong> แจ้งสถานะจัดส่งพัสดุ และรองรับการเคลมเปลี่ยนคืนสินค้ากรณีชำรุดเสียหายใน 7 วัน</span>
                    </div>
                  </div>
                </div>

                {/* Section 3: Non-disclosure Commitment */}
                <div className="p-4 rounded-2xl bg-slate-900 text-white flex items-center gap-3.5 shadow-md">
                  <ShieldCheck className="w-8 h-8 text-emerald-400 shrink-0" />
                  <div className="text-xs sm:text-sm">
                    <strong className="block text-emerald-300 font-bold mb-0.5">การันตีความปลอดภัยและไม่เปิดเผยข้อมูลแก่บุคคลภายนอก</strong>
                    <span>เราไม่มีนโยบายการจำหน่าย แลกเปลี่ยน หรือส่งต่อข้อมูลส่วนบุคคลของท่านให้แก่บุคคลที่สามเพื่อการโฆษณาสแปมโดยเด็ดขาด</span>
                  </div>
                </div>
              </div>
            )}

            {/* 2. PDPA Tab (Rewritten & Redesigned) */}
            {activeTab === 'pdpa' && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <div className="p-5 rounded-2xl bg-gradient-to-r from-emerald-50 to-slate-50 border border-emerald-200 text-emerald-950 flex items-start gap-3.5 shadow-2xs">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-base sm:text-lg text-slate-900 mb-1">
                      การปฏิบัติตาม พ.ร.บ. คุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 (PDPA)
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600 font-medium">
                      คุ้มครองสิทธิและความโปร่งใสให้แก่เจ้าของข้อมูลส่วนบุคคล (Data Subject) ทุกขั้นตอน
                    </p>
                  </div>
                </div>

                {/* Rights Grid */}
                <div className="space-y-4">
                  <h4 className="font-bold text-slate-900 text-base sm:text-lg flex items-center gap-2 border-b border-slate-100 pb-2">
                    <Award className="w-5 h-5 text-emerald-600" />
                    <span>สิทธิขั้นพื้นฐานของท่านตามกฎหมาย PDPA</span>
                  </h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 hover:border-emerald-300 transition-colors">
                      <strong className="text-slate-900 text-sm block mb-1 flex items-center gap-1.5">
                        <Check className="w-4 h-4 text-emerald-600" />
                        สิทธิการเข้าถึงและรับสำเนา (Right of Access)
                      </strong>
                      <p className="text-xs text-slate-600">ท่านมีสิทธิเรียกดูและขอรับสำเนาข้อมูลส่วนบุคคลของท่านในระบบของเรา</p>
                    </div>

                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 hover:border-emerald-300 transition-colors">
                      <strong className="text-slate-900 text-sm block mb-1 flex items-center gap-1.5">
                        <Check className="w-4 h-4 text-emerald-600" />
                        สิทธิขอแก้ไขข้อมูลให้ถูกต้อง (Right to Rectification)
                      </strong>
                      <p className="text-xs text-slate-600">สามารถแจ้งปรับปรุงข้อมูลชื่อ เบอร์ติดต่อ หรือที่อยู่ให้ถูกต้องตรงตามจริงได้เสมอ</p>
                    </div>

                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 hover:border-emerald-300 transition-colors">
                      <strong className="text-slate-900 text-sm block mb-1 flex items-center gap-1.5">
                        <Check className="w-4 h-4 text-emerald-600" />
                        สิทธิขอให้ลบหรือทำลายข้อมูล (Right to Erasure)
                      </strong>
                      <p className="text-xs text-slate-600">สามารถร้องขอให้ลบข้อมูลเมื่อไม่มีความจำเป็นต้องเก็บรักษาตามข้อกฎหมาย</p>
                    </div>

                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 hover:border-emerald-300 transition-colors">
                      <strong className="text-slate-900 text-sm block mb-1 flex items-center gap-1.5">
                        <Check className="w-4 h-4 text-emerald-600" />
                        สิทธิในการถอนความยินยอม (Right to Withdraw Consent)
                      </strong>
                      <p className="text-xs text-slate-600">ท่านสามารถเปลี่ยนแปลงหรือยกเลิกความยินยอมการรับข้อมูลข่าวสารได้ทุกเมื่อ</p>
                    </div>
                  </div>
                </div>

                {/* DPO Contact Details */}
                <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-3">
                  <h4 className="font-bold text-slate-900 text-sm sm:text-base flex items-center gap-2">
                    <UserCheck className="w-5 h-5 text-emerald-600" />
                    <span>ช่องทางติดต่อเจ้าหน้าที่ผู้ดูแลข้อมูลส่วนบุคคล (DPO Contact):</span>
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-slate-600 pt-1">
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                      <strong className="block text-slate-800 mb-0.5">หน่วยงานดูแล:</strong>
                      <span>{distributor.distributorName || 'ศูนย์ตัวแทนจำหน่ายกิฟฟารีนออนไลน์'}</span>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                      <strong className="block text-slate-800 mb-0.5">อีเมลเจ้าหน้าที่คุ้มครองข้อมูล:</strong>
                      <a href="mailto:dlgiffarine@gmail.com" className="text-pink-600 hover:underline font-semibold">
                        dlgiffarine@gmail.com
                      </a>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                      <strong className="block text-slate-800 mb-0.5">LINE Official Account:</strong>
                      <span className="font-bold text-emerald-700">@997wvsow</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 3. Cookie Policy Tab (Rewritten & Redesigned) */}
            {activeTab === 'cookie' && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <div className="p-5 rounded-2xl bg-gradient-to-r from-amber-50 to-slate-50 border border-amber-200 text-amber-950 flex items-start gap-3.5 shadow-2xs">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                    <Cookie className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-base sm:text-lg text-slate-900 mb-1">
                      นโยบายการใช้งานไฟล์คุกกี้ (Cookie Policy)
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600 font-medium">
                      คำอธิบายเกี่ยวกับประเภทและบทบาทของคุกกี้ในการเพิ่มประสิทธิภาพการช้อปปิ้งออนไลน์
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-bold text-slate-900 text-base sm:text-lg flex items-center gap-2 border-b border-slate-100 pb-2">
                    <Cookie className="w-5 h-5 text-amber-600" />
                    <span>บทบาทและประเภทของคุกกี้ที่เราใช้งาน</span>
                  </h4>
                  <p className="text-slate-600 text-sm">
                    คุกกี้ช่วยให้ระบบเว็บไซต์จดจำสถานะการใช้งานของคุณ อำนวยความสะดวกให้ไม่ต้องกรอกข้อมูลซ้ำซ้อน และช่วยให้การทำงานของระบบคำนวณราคาและส่วนลดเป็นไปอย่างราบรื่น:
                  </p>

                  <div className="space-y-3 pt-1">
                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                      <div className="flex items-center justify-between mb-1.5">
                        <strong className="text-slate-900 text-sm">1. คุกกี้ความจำเป็นหลัก (Essential System Cookies)</strong>
                        <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-slate-200 text-slate-700">จำเป็นต่อระบบ</span>
                      </div>
                      <p className="text-xs text-slate-600">
                        ควบคุมการทำงานของระบบตะกร้าสินค้า การบันทึกรายการสั่งซื้อ และระบบความปลอดภัยในการส่งข้อมูล
                      </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                      <div className="flex items-center justify-between mb-1.5">
                        <strong className="text-slate-900 text-sm">2. คุกกี้การวัดผลและประสิทธิภาพ (Analytics & Performance)</strong>
                        <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-blue-100 text-blue-800">เลือกกำหนดได้</span>
                      </div>
                      <p className="text-xs text-slate-600">
                        ช่วยรวบรวมสถิติความเร็วในการโหลดหน้าเว็บ เพื่อนำไปพัฒนาและปรับปรุงประสบการณ์การใช้งานให้ดียิ่งขึ้น
                      </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                      <div className="flex items-center justify-between mb-1.5">
                        <strong className="text-slate-900 text-sm">3. คุกกี้ข้อเสนอพิเศษและสิทธิประโยชน์ (Personalized Offers)</strong>
                        <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-purple-100 text-purple-800">เลือกกำหนดได้</span>
                      </div>
                      <p className="text-xs text-slate-600">
                        ช่วยแสดงโปรโมชั่นสินค้ากิฟฟารีนที่สอดคล้องกับความสนใจและสิทธิพิเศษราคาสมาชิกของคุณ
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-2 text-center">
                  <button
                    onClick={() => handleTabChange('cookie-settings')}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs sm:text-sm shadow-md transition-all active:scale-95 cursor-pointer"
                  >
                    <Sliders className="w-4 h-4 text-pink-400" />
                    <span>เข้าสู่หน้าปรับแต่งการตั้งค่าคุกกี้ (Cookie Settings)</span>
                  </button>
                </div>
              </div>
            )}

            {/* 4. Interactive Cookie Settings Tab (Redesigned) */}
            {activeTab === 'cookie-settings' && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <div className="p-5 rounded-2xl bg-gradient-to-r from-pink-50 to-slate-50 border border-pink-200 text-pink-950 flex items-start gap-3.5 shadow-2xs">
                  <div className="w-10 h-10 rounded-xl bg-pink-100 text-[#E60067] flex items-center justify-center shrink-0">
                    <Sliders className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-base sm:text-lg text-slate-900 mb-1">
                      แผงควบคุมและกำหนดค่าความยินยอมคุกกี้
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600 font-medium">
                      ปรับแต่งความยินยอมในการบันทึกข้อมูลคุกกี้ตามที่คุณต้องการได้ตลอดเวลา
                    </p>
                  </div>
                </div>

                {saveSuccessMessage && (
                  <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-300 text-emerald-900 flex items-center gap-2.5 animate-in slide-in-from-top-2 duration-200">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    <span className="text-xs sm:text-sm font-bold">บันทึกการตั้งค่าความยินยอมของคุณเรียบร้อยแล้ว การตั้งค่ามีผลทันที</span>
                  </div>
                )}

                <div className="space-y-4">
                  {/* Category 1: Strictly Necessary */}
                  <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 shadow-2xs">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <strong className="text-slate-900 text-sm sm:text-base flex items-center gap-2">
                          <Lock className="w-4 h-4 text-slate-700" />
                          คุกกี้ความจำเป็นหลัก (Strictly Necessary)
                        </strong>
                        <p className="text-xs text-slate-500 mt-1">
                          จำเป็นต่อการสั่งซื้อ การจัดการสินค้าในตะกร้า และการยืนยันตัวตน
                        </p>
                      </div>
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-white text-slate-700 shrink-0 border border-slate-200">
                        เปิดใช้งานเสมอ
                      </span>
                    </div>
                  </div>

                  {/* Category 2: Analytics */}
                  <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-2xs hover:border-pink-200 transition-colors">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <strong className="text-slate-900 text-sm sm:text-base flex items-center gap-2">
                          <Sliders className="w-4 h-4 text-blue-600" />
                          คุกกี้เพื่อการวิเคราะห์และพัฒนา (Analytics)
                        </strong>
                        <p className="text-xs text-slate-500 mt-1">
                          ช่วยให้เราทราบสถิติการใช้งานเพื่อนำไปปรับปรุงความเร็วและระบบให้มีประสิทธิภาพสูงขึ้น
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer shrink-0">
                        <input
                          type="checkbox"
                          checked={analyticsCookies}
                          onChange={(e) => setAnalyticsCookies(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-12 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#E60067]" />
                      </label>
                    </div>
                  </div>

                  {/* Category 3: Marketing */}
                  <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-2xs hover:border-pink-200 transition-colors">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <strong className="text-slate-900 text-sm sm:text-base flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-purple-600" />
                          คุกกี้เพื่อข้อเสนอพิเศษ (Marketing & Personalization)
                        </strong>
                        <p className="text-xs text-slate-500 mt-1">
                          ช่วยแนะนำโปรโมชั่นและส่วนลดผลิตภัณฑ์กิฟฟารีนที่เหมาะสมกับคุณ
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer shrink-0">
                        <input
                          type="checkbox"
                          checked={marketingCookies}
                          onChange={(e) => setMarketingCookies(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-12 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#E60067]" />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-200">
                  <button
                    onClick={handleResetCookieSettings}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs sm:text-sm transition-all active:scale-95 cursor-pointer"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span>คืนค่าเริ่มต้น (เปิดใช้งานทั้งหมด)</span>
                  </button>

                  <button
                    onClick={handleSaveCookieSettings}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs sm:text-sm shadow-md transition-all active:scale-95 cursor-pointer"
                  >
                    <Check className="w-4 h-4 text-emerald-400" />
                    <span>บันทึกการตั้งค่า (Save Settings)</span>
                  </button>
                </div>
              </div>
            )}

            {/* 5. Terms of Service Tab (Rewritten & Redesigned) */}
            {activeTab === 'terms' && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <div className="p-5 rounded-2xl bg-gradient-to-r from-blue-50 to-slate-50 border border-blue-200 text-blue-950 flex items-start gap-3.5 shadow-2xs">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-base sm:text-lg text-slate-900 mb-1">
                      ข้อตกลงและเงื่อนไขการใช้บริการ (Terms of Service)
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600 font-medium">
                      มาตรฐานการให้บริการ การรับประกันสินค้าแท้ และกติกาการทำรายการสั่งซื้อผ่านแพลตฟอร์ม
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-bold text-slate-900 text-base sm:text-lg flex items-center gap-2 border-b border-slate-100 pb-2">
                    <Scale className="w-5 h-5 text-blue-600" />
                    <span>สาระสำคัญในการสั่งซื้อและใช้บริการ</span>
                  </h4>
                  <div className="space-y-3 text-sm text-slate-600">
                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                      <strong className="block text-slate-900 text-sm mb-1">1. มาตรฐานสินค้าของแท้ 100% สดใหม่จากคลัง:</strong>
                      <p className="text-xs text-slate-600">
                        ผลิตภัณฑ์ทุกชิ้นได้รับการจัดส่งตรงจากศูนย์ธุรกิจกิฟฟารีนสาขาเดลิเวอรี่ ผ่านการรับรองมาตรฐาน อย. และมีการควบคุมวันหมดอายุอย่างเคร่งครัด
                      </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                      <strong className="block text-slate-900 text-sm mb-1">2. สิทธิพิเศษการสมัครสมาชิก 180 บาทตลอดชีพ:</strong>
                      <p className="text-xs text-slate-600">
                        ผู้สมัครจะได้รับสิทธิ์สั่งซื้อสินค้าในราคาสมาชิก (ลด 25%) ทุกรายการตลอดชีพ พร้อมสิทธิ์รับเงินปันผลตามแผนงานกิฟฟารีนโดยไม่มีเงื่อนไขบังคับยอด
                      </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                      <strong className="block text-slate-900 text-sm mb-1">3. ความปลอดภัยในการทำธุรกรรมชำระเงิน:</strong>
                      <p className="text-xs text-slate-600">
                        รองรับการโอนผ่าน พร้อมเพย์ QR Dynamic และบริการเก็บเงินปลายทาง (COD) เพื่อความสะดวก มั่นใจ และปลอดภัยสูงสุดของผู้ซื้อ
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 6. Shipping & 7-Day Guarantee Tab (Rewritten & Redesigned) */}
            {activeTab === 'shipping' && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <div className="p-5 rounded-2xl bg-gradient-to-r from-rose-50 to-slate-50 border border-rose-200 text-rose-950 flex items-start gap-3.5 shadow-2xs">
                  <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
                    <Truck className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-base sm:text-lg text-slate-900 mb-1">
                      นโยบายการจัดส่งสินค้า และการรับประกันความพึงพอใจ 7 วัน
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600 font-medium">
                      มาตรฐานการขนส่งด่วนทั่วประเทศ พร้อมความคุ้มครองเปลี่ยนคืนสินค้าชำรุดเสียหาย
                    </p>
                  </div>
                </div>

                {/* Delivery Timeline Details */}
                <div className="space-y-4">
                  <h4 className="font-bold text-slate-900 text-base sm:text-lg flex items-center gap-2 border-b border-slate-100 pb-2">
                    <Truck className="w-5 h-5 text-rose-600" />
                    <span>กำหนดเวลาและช่องทางจัดส่งด่วน</span>
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                      <div className="flex items-center gap-2 text-slate-900 font-bold text-sm mb-1">
                        <Clock className="w-4 h-4 text-rose-600" />
                        <span>ความรวดเร็วในการนำส่ง</span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        • กทม. และปริมณฑล: ได้รับใน 1-2 วันทำการ<br />
                        • ต่างจังหวัด: ได้รับใน 2-3 วันทำการ<br />
                        • มีรหัสติดตามพัสดุ (Tracking) ส่งแจ้งผ่าน SMS/LINE
                      </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                      <div className="flex items-center gap-2 text-slate-900 font-bold text-sm mb-1">
                        <PackageCheck className="w-4 h-4 text-rose-600" />
                        <span>มาตรฐานการแพ็คสินค้า</span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        บรรจุห่อกันกระแทกอย่างหนาแน่นทุกชิ้น ดำเนินการโดยเจ้าหน้าที่ประจำสาขากิฟฟารีนโดยตรง มั่นใจสินค้าไม่แตกหัก
                      </p>
                    </div>
                  </div>
                </div>

                {/* 7-Day Guarantee Steps */}
                <div className="space-y-4">
                  <h4 className="font-bold text-slate-900 text-base sm:text-lg flex items-center gap-2 border-b border-slate-100 pb-2">
                    <Shield className="w-5 h-5 text-rose-600" />
                    <span>ขั้นตอนการเคลมเปลี่ยนคืนสินค้าภายใน 7 วัน</span>
                  </h4>
                  <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                    <p className="text-xs sm:text-sm text-slate-700">
                      หากพบกรณีพัสดุชำรุดเสียหายระหว่างขนส่ง หรือได้รับสินค้าไม่ถูกต้อง สามารถติดต่อขอเปลี่ยนชิ้นใหม่ได้ภายใน <strong>7 วันทำการ</strong>:
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1 text-xs text-slate-700">
                      <div className="p-3.5 bg-white rounded-xl border border-slate-200 shadow-2xs">
                        <strong className="block text-slate-900 mb-1">ขั้นตอนที่ 1: บันทึกภาพ</strong>
                        <span>ถ่ายภาพหรือคลิปวิดีโอกล่องพัสดุและตัวสินค้าที่มีปัญหา</span>
                      </div>
                      <div className="p-3.5 bg-white rounded-xl border border-slate-200 shadow-2xs">
                        <strong className="block text-slate-900 mb-1">ขั้นตอนที่ 2: แจ้งแอดมิน</strong>
                        <span>ส่งหลักฐานแจ้งทาง LINE OA <strong className="text-emerald-700">@997wvsow</strong></span>
                      </div>
                      <div className="p-3.5 bg-white rounded-xl border border-slate-200 shadow-2xs">
                        <strong className="block text-slate-900 mb-1">ขั้นตอนที่ 3: รับสินค้าใหม่</strong>
                        <span>เจ้าหน้าที่จัดส่งชิ้นใหม่ให้ทันทีโดยไม่มีค่าใช้จ่ายเพิ่มเติม</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* Quick Footer Shortcuts */}
          <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <span className="text-xs text-slate-500 font-medium">
              เว็บไซต์ทางการ: <strong className="text-slate-800">{domain}</strong>
            </span>
            <div className="flex items-center gap-2.5 flex-wrap">
              <button
                onClick={() => navigateTo('/aboutus')}
                className="px-4 py-2 rounded-xl bg-white hover:bg-slate-100 text-slate-700 text-xs font-bold border border-slate-200 transition-all cursor-pointer"
              >
                เกี่ยวกับเรา (About Us)
              </button>
              <button
                onClick={() => navigateTo('/member-privilege')}
                className="px-4 py-2 rounded-xl bg-pink-50 hover:bg-pink-100 text-[#E60067] text-xs font-bold border border-pink-200 transition-all cursor-pointer"
              >
                สมัครกิฟฟารีน (180 บาท)
              </button>
              <button
                onClick={onNavigateHome}
                className="px-6 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs sm:text-sm font-bold shadow-xs transition-all active:scale-95 cursor-pointer"
              >
                กลับสู่หน้าหลัก
              </button>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};
